// Shared utility functions for photo operations
import { VIDEO_MIME_TYPES } from '../../../utils/mediaFileType';

// Every video extension the API accepts, from the same table the upload check uses (#397), so a
// video that can be uploaded is also recognised as one in the gallery.
const VIDEO_EXTENSIONS = new Set(Object.keys(VIDEO_MIME_TYPES).map((extension) => `.${extension}`));

/**
 * Check if a filename is a video based on extension
 * @param {string} filename - The filename to check
 * @returns {boolean} True if the file is a video
 */
export const isVideo = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return false;
  }
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return VIDEO_EXTENSIONS.has(extension);
};

/**
 * Get file type from filename
 * @param {string} filename - The filename to check
 * @returns {string} 'video' or 'image'
 */
export const getFileType = (filename) => {
  return isVideo(filename) ? 'video' : 'image';
};

export const shouldContinueVideoPlayback = ({
  isPlaying,
  playbackGeneration,
  activeGeneration,
  slideIndex,
  activeSlideIndex,
}) => isPlaying
  && playbackGeneration === activeGeneration
  && slideIndex === activeSlideIndex;

export const shouldStartVideoPlayback = ({ isActive, isPlaying, streamUrl, videoElement }) =>
  Boolean(isActive && isPlaying && streamUrl && videoElement
    && videoElement.readyState >= 2
    && (videoElement.currentSrc || videoElement.src));

const encodeObjectPath = (objectPath) =>
  String(objectPath || '')
    .split('/')
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');

const getStoragePathParts = (filename, galleryId) => {
  if (!filename || typeof filename !== 'string') {
    return null;
  }

  const normalized = filename.replace(/^\/+/, '');
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length >= 2) {
    const fileWithExt = parts[parts.length - 1];
    const mediaId = fileWithExt.replace(/\.[^/.]+$/, '');
    if (!mediaId) {
      return null;
    }
    return {
      galleryId: parts[0],
      mediaId,
      originalPath: parts.join('/'),
    };
  }

  if (galleryId) {
    const mediaId = normalized.replace(/\.[^/.]+$/, '');
    if (!mediaId) {
      return null;
    }
    return {
      galleryId,
      mediaId,
      originalPath: null,
    };
  }

  return null;
};

const getMediaStorageKey = (mediaItem) => {
  if (!mediaItem || typeof mediaItem === 'string') {
    return mediaItem;
  }

  return mediaItem.originalPath || mediaItem.filename || '';
};

/**
 * Generate thumbnail URL for a photo/video
 * @param {string} filename - The filename
 * @returns {string} The thumbnail URL
 */
export const getThumbnailUrl = (filename, galleryId) => {
  if (!filename || typeof filename !== 'string') {
    return '';
  }
  const storageParts = getStoragePathParts(filename, galleryId);
  if (storageParts) {
    return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/${encodeURIComponent(storageParts.galleryId)}/${encodeURIComponent(storageParts.mediaId)}/thumb.webp`;
  }
  const baseName = filename.replace(/\.[^/.]+$/, '');
  return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/thumbnails/${encodeURIComponent(baseName)}.webp`;
};

/**
 * Generate original file URL
 * @param {string} filename - The filename
 * @returns {string} The original file URL
 */
export const getOriginalUrl = (filename) => {
  if (filename && typeof filename === 'string' && filename.includes('/')) {
    return `https://storage.googleapis.com/${process.env.REACT_APP_ORIGINAL_BUCKET}/${encodeObjectPath(filename)}`;
  }
  // URL encode the filename to handle special characters
  return `https://storage.googleapis.com/${process.env.REACT_APP_ORIGINAL_BUCKET}/original/${encodeURIComponent(filename)}`;
};

/**
 * Generate processed photo URL (webp version)
 * @param {string} filename - The filename
 * @returns {string} The processed photo URL
 */
export const getProcessedPhotoUrl = (filename, galleryId) => {
  const storageParts = getStoragePathParts(filename, galleryId);
  if (storageParts) {
    return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/${encodeURIComponent(storageParts.galleryId)}/${encodeURIComponent(storageParts.mediaId)}/full.webp`;
  }
  const baseName = filename.replace(/\.[^/.]+$/, '');
  // URL encode the baseName to handle special characters
  return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/photos/${encodeURIComponent(baseName)}.webp`;
};

export const getMediaDownloadKey = (mediaItem) => getMediaStorageKey(mediaItem);

export const isMediaProcessed = (mediaItem) => Boolean(
  mediaItem && typeof mediaItem === 'object' && mediaItem.processed
);

export const getMediaFileType = (mediaItem) => {
  if (mediaItem && typeof mediaItem === 'object' && mediaItem.type) {
    return mediaItem.type === 'video' ? 'video' : 'image';
  }

  return getFileType(getMediaStorageKey(mediaItem));
};

export const getMediaThumbnailUrl = (mediaItem, galleryId) => {
  if (!mediaItem) {
    return '';
  }

  if (typeof mediaItem === 'object') {
    if (mediaItem.thumbnailUrl) {
      return mediaItem.thumbnailUrl;
    }
    if (mediaItem.previewUrl) {
      return mediaItem.previewUrl;
    }
    if (mediaItem.thumbnailPath) {
      return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/${encodeObjectPath(mediaItem.thumbnailPath)}`;
    }
    if (mediaItem.processedPath) {
      return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/${encodeObjectPath(mediaItem.processedPath)}`;
    }
  }

  return getThumbnailUrl(getMediaStorageKey(mediaItem), galleryId);
};

export const getMediaProcessedPhotoUrl = (mediaItem, galleryId) => {
  if (mediaItem && typeof mediaItem === 'object') {
    if (mediaItem.previewUrl) {
      return mediaItem.previewUrl;
    }
    if (mediaItem.processedPath) {
      return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/${encodeObjectPath(mediaItem.processedPath)}`;
    }
  }

  return getProcessedPhotoUrl(getMediaStorageKey(mediaItem), galleryId);
};

export const getFloorplanUrl = (filename) => {
  return `https://storage.googleapis.com/${process.env.REACT_APP_PROCESSED_BUCKET}/floorplan.webp`;
};
